import { useState } from 'react';
import { PenLine, ShieldAlert, UserCog } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatDateShort } from '../../lib/format';
import type { TimeChange } from './api';

/**
 * ⚠ **SOMEBODY ELSE'S HANDWRITING ON YOUR TIMESHEET — THE THREE PLACES IT SHOWS.**
 *
 * An approver may open and rewrite anyone's DRAFT week; that is what an
 * approver is for and it stays allowed. It used to be *invisible*: `TimeEntry`
 * carried `createdAt`/`updatedAt` and nothing else, the reviewing screen was
 * pixel-identical to your own week (same header, same "Saved", same toast),
 * and the topbar cheerfully offered "Start timer for Andre" one click from a
 * manager's cursor. So Andre could not learn his Wednesday had moved, and the
 * invoice that billed it had no provenance.
 *
 * Three surfaces close it, and none of them costs a click:
 *   1. {@link ReviewingBanner} — you are on somebody else's sheet, and it says
 *      so *before* you type, with the grid tinted underneath it.
 *   2. {@link ChangesNotice} — the OWNER is told, on the week they open, what
 *      was done to it and by whom. Collapsed to one sentence; the trail is one
 *      disclosure away, never behind a route.
 *   3. {@link EditedByMark} — the individual hour carries its author.
 *
 * All three read server fields (`WeekData.changes`, `TimeEntry.editedBy`); the
 * browser never infers an author.
 */

/** The tint a grid wears while it is showing a sheet that is not yours. */
export const FOREIGN_SHEET_TINT = 'bg-accent/[0.03] ring-1 ring-inset ring-accent/20';

export function ReviewingBanner({
  ownerName,
  locked,
  className,
}: {
  ownerName: string;
  /** A submitted/approved week takes no writes at all — say the softer thing. */
  locked: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mb-3 flex items-start gap-2.5 rounded-xl border border-accent/30 bg-accent/[0.06] px-3 py-2.5 sm:px-4',
        className,
      )}
    >
      <UserCog className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
      <div className="min-w-0">
        <p className="text-body-sm font-bold leading-snug text-secondary">
          You are reviewing {ownerName}&rsquo;s timesheet
        </p>
        <p className="mt-0.5 text-caption leading-snug text-grey-600">
          {locked ? (
            <>
              These are {ownerName}&rsquo;s hours, and the week is read-only while it is under
              review. Nothing here changes {ownerName}&rsquo;s record.
            </>
          ) : (
            <>
              Anything you change here is {ownerName}&rsquo;s billable time. Every edit is recorded
              with your name and shown to {ownerName} on this week — it is not a private
              correction. The timer is theirs to start, not yours.
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * "Changed by Priya Raman · Aug 13" — the marker on one hour.
 *
 * ONE MARKER, WHEREVER A BILLABLE HOUR IS SHOWN. It is deliberately not a
 * timesheet-only component: the invoice's billed-work table renders the same
 * chip on the same hours, because that table is the audit trail an invoice is
 * defended with. Its prop is the minimum both surfaces have (`name` + `at`),
 * so neither has to reshape the other's payload to reuse the glyph.
 */
export function EditedByMark({
  editedBy,
  detail,
  className,
  compact,
}: {
  editedBy: { name: string; at: string | null };
  /**
   * The sentence recorded at the moment of the write, when the caller has it
   * (the invoice audit view does; a grid cell does not). Shown in the tooltip
   * under the summary line — never re-derived in the browser.
   */
  detail?: string | null;
  className?: string;
  /** Glyph only (inside a 64px grid cell); the words live in the tooltip. */
  compact?: boolean;
}) {
  const when = editedBy.at ? formatDateShort(editedBy.at) : null;
  const full =
    `Changed by ${editedBy.name}${when ? ` · ${when}` : ''} — not by the person whose timesheet this is.` +
    (detail ? `\n${detail}` : '');
  if (compact) {
    return (
      <span
        title={full}
        className={cn('flex items-center text-accent-700', className)}
        aria-label={full}
      >
        <PenLine className="h-2.5 w-2.5" aria-hidden />
      </span>
    );
  }
  return (
    <span
      title={full}
      className={cn(
        'inline-flex items-center gap-1 rounded bg-accent/10 px-1.5 py-px text-[10px] font-bold leading-4 text-accent-700',
        className,
      )}
    >
      <PenLine className="h-2.5 w-2.5 shrink-0" aria-hidden />
      Changed by {editedBy.name}
      {when && <span className="font-semibold text-accent-700/80">· {when}</span>}
    </span>
  );
}

/**
 * The owner's notice: what somebody else did to this week.
 *
 * Shown to EVERYONE who opens the week — the owner needs to know, and an
 * approver arriving second needs to know another approver was here. Collapsed
 * by default to one counted sentence, because a week nobody touched must not
 * grow a block of chrome; the sentences themselves were written on the server
 * at the moment of each write and are never re-derived.
 */
export function ChangesNotice({
  changes,
  ownerName,
  isOwnWeek,
  className,
}: {
  changes: TimeChange[];
  ownerName: string;
  isOwnWeek: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (changes.length === 0) return null;
  const actors = [...new Set(changes.map((c) => c.actorName ?? 'Another user'))];
  const who =
    actors.length === 1
      ? actors[0]
      : `${actors.slice(0, -1).join(', ')} and ${actors[actors.length - 1]}`;

  return (
    <div
      className={cn(
        'mb-3 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2.5 sm:px-4',
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-status-partial" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-body-sm font-bold leading-snug text-secondary">
            {who} changed {changes.length === 1 ? 'an entry' : `${changes.length} entries`} on{' '}
            {isOwnWeek ? 'your' : `${ownerName}’s`} timesheet
          </p>
          <p className="mt-0.5 text-caption leading-snug text-grey-600">
            {isOwnWeek
              ? 'These hours were not written by you. Check them before you submit the week — they are what gets invoiced.'
              : `Another reviewer has already edited this week. ${ownerName} can see this too.`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="shrink-0 rounded-md px-2 py-1 text-caption font-bold text-status-partial transition-colors hover:bg-warning/20"
        >
          {open ? 'Hide' : `Show ${changes.length === 1 ? 'it' : 'them'}`}
        </button>
      </div>
      {open && (
        <ul className="mt-2 space-y-1.5 border-t border-warning/30 pt-2">
          {changes.map((change) => (
            <li key={change.id} className="flex items-start gap-2 text-caption leading-snug">
              <PenLine className="mt-0.5 h-3 w-3 shrink-0 text-status-partial" aria-hidden />
              <span className="min-w-0 text-grey-600">
                {change.summary}{' '}
                <span className="whitespace-nowrap text-grey-400">
                  ({formatDateShort(change.at)})
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
